import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { formatBRL } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/membros/calculadora-10k")({
  head: () => ({ meta: [{ title: "Calculadora 10K — Projeto 101" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CalcPage,
});

function CalcPage() {
  const [ticket, setTicket] = useState(350);
  const [goal, setGoal] = useState(10000);
  const [days, setDays] = useState(22);
  const [conv, setConv] = useState(25);
  const [varCost, setVarCost] = useState(40);
  const [fixedCost, setFixedCost] = useState(500);

  const result = useMemo(() => {
    const sales = Math.max(1, Math.ceil(goal / Math.max(1, ticket)));
    const perDay = sales / Math.max(1, days);
    const leads = Math.ceil(sales / Math.max(0.01, conv / 100));
    const gross = sales * ticket;
    const varTotal = sales * varCost;
    const margin = gross - varTotal - fixedCost;
    const breakEven = Math.ceil((fixedCost + 0) / Math.max(1, ticket - varCost));
    return { sales, perDay, leads, gross, varTotal, margin, breakEven };
  }, [ticket, goal, days, conv, varCost, fixedCost]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">Calculadora 10K</h1>
        <p className="mt-1 text-sm text-[var(--gray-ink)]">
          Simule quantos clientes você precisa para atingir sua meta de faturamento.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="font-display text-lg font-extrabold">Parâmetros</h2>
          <div className="mt-4 space-y-3">
            <Field label="Ticket médio (R$)" value={ticket} setValue={setTicket} />
            <Field label="Meta mensal de faturamento bruto (R$)" value={goal} setValue={setGoal} />
            <Field label="Dias trabalhados no mês" value={days} setValue={setDays} />
            <Field label="Conversão estimada (%)" value={conv} setValue={setConv} />
            <Field label="Custo variável por venda (R$)" value={varCost} setValue={setVarCost} />
            <Field label="Custo fixo mensal (R$)" value={fixedCost} setValue={setFixedCost} />
          </div>
        </div>
        <div className="rounded-2xl border border-black/5 bg-white p-6">
          <h2 className="font-display text-lg font-extrabold">Resultado</h2>
          <div className="mt-4 space-y-3 text-sm">
            <Line label="Vendas necessárias" value={String(result.sales)} />
            <Line label="Vendas por dia" value={result.perDay.toFixed(1)} />
            <Line label="Leads estimados" value={String(result.leads)} />
            <Line label="Faturamento bruto" value={formatBRL(result.gross * 100)} />
            <Line label="Custos variáveis" value={formatBRL(result.varTotal * 100)} />
            <Line label="Margem estimada (antes de impostos)" value={formatBRL(result.margin * 100)} highlight />
            <Line label="Ponto de equilíbrio (vendas)" value={String(result.breakEven)} />
          </div>
        </div>
      </div>

      <p className="rounded-lg bg-[var(--beige)] p-4 text-xs text-[var(--gray-ink)]">
        Esta ferramenta apresenta simulações de planejamento. Os resultados reais variam e dependem da execução e do mercado.
      </p>
    </div>
  );
}

function Field({ label, value, setValue }: { label: string; value: number; setValue: (n: number) => void }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full rounded-md border border-black/10 bg-white px-3 py-2 outline-none focus:border-[var(--green)]"
      />
    </label>
  );
}

function Line({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between border-b border-black/5 py-2 last:border-0 ${highlight ? "text-[var(--green)]" : ""}`}>
      <span className="text-[var(--gray-ink)]">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
